import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { getHubConfig, getListingConfig } from '../lib/pageConfig';
import {
  mergePageConfig,
  convertApiBlocksToCmsBlocks,
  groupBlocksByZone,
  getCmsSlugFromPath,
  DEFAULT_GLOBAL_SETTINGS
} from '../lib/cmsOverlayService';

/**
 * useCmsOverlay - Hook pour charger et fusionner les overrides CMS
 * 
 * ARCHITECTURE SCOPE-DRIVEN:
 * 1. Charge la config système (pageConfig.js)
 * 2. Charge les blocs CMS depuis l'API
 * 3. FILTRE par scope (HUB vs LISTING) - ISOLATION STRICTE
 * 4. Retourne la config finale + blocs par zone
 * 
 * RÈGLES D'ISOLATION:
 * - HUB pages: uniquement blocs avec scope='HUB' ou scope non défini sur page HUB
 * - LISTING pages: uniquement blocs avec scope='LISTING' ou scope non défini sur page LISTING
 * - Aucun fallback UI - si CMS vide, retourne []
 * 
 * @param {string} pageType - 'hub' ou 'listing'
 * @param {string} pageId - ID de la config (ex: 'nouveautes', 'nouveautes-en-cours')
 * @param {Object} options - Options supplémentaires
 * @returns {Object} { config, cmsBlocks, blocksByZone, globalSettings, loading, error }
 */
const useCmsOverlay = (pageType, pageId, options = {}) => {
  const [cmsData, setCmsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { enableCms = true, debug = false } = options;

  // Récupérer la config système
  const systemConfig = useMemo(() => {
    if (pageType === 'hub') {
      return getHubConfig(pageId);
    } else if (pageType === 'listing') {
      return getListingConfig(pageId);
    }
    return null;
  }, [pageType, pageId]);

  /**
   * SCOPE FILTER - Isolation stricte HUB vs LISTING
   * 
   * RÈGLES:
   * - Blocs HUB shadow page (hub-xxx) → scope implicite 'HUB'
   * - Blocs LISTING → scope implicite 'LISTING'
   * - Blocs avec scope explicite → respecter le scope
   * - Blocs sans scope → utiliser le scope de la page source
   */
  const filterBlocksByScope = (blocks, targetScope, sourcePageId) => {
    if (!blocks || blocks.length === 0) return [];
    
    const normalizedTarget = targetScope.toUpperCase();
    const isHubSource = sourcePageId?.startsWith('hub-');
    
    return blocks.filter(block => {
      // Si le bloc a un scope explicite, l'utiliser
      if (block.scope) {
        return block.scope.toUpperCase() === normalizedTarget;
      }
      
      // Sinon, déduire le scope de la source
      // Les blocs d'une page HUB shadow sont implicitement HUB scope
      if (isHubSource) {
        return normalizedTarget === 'HUB';
      }
      
      // Les blocs d'une page LISTING sont implicitement LISTING scope
      return normalizedTarget === 'LISTING';
    });
  };

  // Charger les données CMS
  useEffect(() => {
    const loadCmsData = async () => {
      if (!enableCms || !pageId) {
        setLoading(false);
        setCmsData({ enabled: false, blocks: [] });
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        let pageData = null;
        let sourcePageId = null;
        
        // === STRATÉGIE DE CHARGEMENT SCOPE-AWARE ===
        
        if (pageType === 'hub') {
          // Pour HUB: charger UNIQUEMENT la shadow page HUB
          // NE PAS fallback sur d'autres pages CMS
          const hubShadowId = `hub-${pageId}`;
          sourcePageId = hubShadowId;
          
          try {
            if (debug) {
              console.log(`[useCmsOverlay] HUB scope: Loading ${hubShadowId}`);
            }
            const response = await api.get(`/pages/${hubShadowId}`);
            pageData = response.data;
            if (debug) {
              console.log(`[useCmsOverlay] HUB shadow loaded: ${pageData?.blocks?.length || 0} blocks`);
            }
          } catch (hubError) {
            if (hubError.response?.status !== 404) {
              console.warn('[useCmsOverlay] Error loading HUB shadow:', hubError);
            }
            // Pas de fallback - HUB CMS vide = pas de blocs
            pageData = null;
          }
        } else if (pageType === 'listing') {
          // Pour LISTING: charger la page CMS spécifique au listing
          // NE PAS charger les blocs HUB
          const cmsSlug = getCmsSlugFromPath(
            `/dashboard/${pageId.replace(/-/g, '/')}`,
            'listing'
          );
          sourcePageId = `listing-${pageId}`;
          
          if (debug) {
            console.log(`[useCmsOverlay] LISTING scope: Loading ${cmsSlug}`);
          }
          
          try {
            const response = await api.get(`/pages/slug${cmsSlug}`);
            pageData = response.data;
            if (debug) {
              console.log(`[useCmsOverlay] LISTING CMS loaded: ${pageData?.blocks?.length || 0} blocks`);
            }
          } catch (slugError) {
            if (slugError.response?.status === 404) {
              if (debug) {
                console.log(`[useCmsOverlay] No LISTING CMS for ${cmsSlug} - empty blocks`);
              }
            } else {
              throw slugError;
            }
            // Pas de fallback - LISTING CMS vide = pas de blocs
            pageData = null;
          }
        }
        
        // Traiter les données avec filtrage par scope
        if (pageData && pageData.blocks && pageData.blocks.length > 0) {
          const allBlocks = convertApiBlocksToCmsBlocks(pageData.blocks);
          
          // FILTRE CRITIQUE: Isolation par scope
          const scopedBlocks = filterBlocksByScope(
            allBlocks, 
            pageType, // 'hub' ou 'listing'
            sourcePageId
          );
          
          if (debug) {
            console.log(`[useCmsOverlay] Scope filter: ${allBlocks.length} → ${scopedBlocks.length} blocks for ${pageType.toUpperCase()}`);
          }
          
          setCmsData({
            enabled: scopedBlocks.length > 0,
            blocks: scopedBlocks,
            globalSettings: pageData.settings || {},
            sections: pageData.sections || {},
            pageId: pageData.id,
            pageTitle: pageData.title,
            scope: pageType.toUpperCase()
          });
        } else {
          // CMS vide = pas de blocs (pas de fallback!)
          setCmsData({ 
            enabled: false, 
            blocks: [],
            scope: pageType.toUpperCase()
          });
        }
      } catch (err) {
        console.error('[useCmsOverlay] Error loading CMS data:', err);
        setError(err.message);
        setCmsData({ enabled: false, blocks: [], scope: pageType.toUpperCase() });
      } finally {
        setLoading(false);
      }
    };

    loadCmsData();
  }, [pageId, pageType, enableCms, debug]);

  // Fusionner config système + CMS
  const mergedConfig = useMemo(() => {
    if (!systemConfig) return null;
    
    return mergePageConfig(systemConfig, cmsData);
  }, [systemConfig, cmsData]);

  // Grouper les blocs par zone
  const blocksByZone = useMemo(() => {
    if (!mergedConfig?.cmsBlocks) {
      return { header: [], menu: [], content: [], footer: [] };
    }
    return groupBlocksByZone(mergedConfig.cmsBlocks);
  }, [mergedConfig]);

  // Global settings avec fallback
  const globalSettings = useMemo(() => {
    return mergedConfig?.globalSettings || DEFAULT_GLOBAL_SETTINGS;
  }, [mergedConfig]);

  return {
    // Config finale (système + CMS)
    config: mergedConfig,
    
    // Blocs CMS bruts
    cmsBlocks: mergedConfig?.cmsBlocks || [],
    
    // Blocs groupés par zone
    blocksByZone,
    
    // Settings globaux (header/menu/footer visibility)
    globalSettings,
    
    // États de chargement
    loading,
    error,
    
    // Metadata
    hasCmsOverrides: cmsData?.enabled || false,
    cmsPageId: cmsData?.pageId || null
  };
};

export default useCmsOverlay;
